import { useCallback, useEffect, useMemo, useState } from 'react';

import { orderOptoutChannelsQuery } from '../../store/order/selectors';
import { ChannelsAvailable } from '../../store/order/types';
import { FeatureToggle, useUnleashFlags } from '../../utils/useUnleashFlags';
import { useRecoilQuery } from '../useRecoilQuery';

export type SendingChannelItemProps = {
  label: string;
  value: string;
  disabled?: boolean;
  isInvalid?: boolean;
  invalidMessage?: string;
};

export const useSendingChannels = (customerId?: number, forceDisablePush?: boolean) => {
  const { b2bSendingChannelPushOptionEnabled, b2bSendingChannelWhatsAppOptionEnabled } =
    useUnleashFlags(
      FeatureToggle.WhatsAppSendingChannelOptionIsEnabled,
      FeatureToggle.PushSendingChannelOptionIsEnabled
    );

  const { data: channelsData, loading: loadingChannels } = useRecoilQuery(
    orderOptoutChannelsQuery({ customerId })
  );

  const isChannelBlocked = (channel: string, optionsAvailable: ChannelsAvailable) => {
    const isAvailable = optionsAvailable[channel];
    return isAvailable === false;
  };

  const getChannelOptionsAvailable = useCallback(() => {
    const IsDisabledPush = !b2bSendingChannelPushOptionEnabled || forceDisablePush;

    const defaultSendingChannels: SendingChannelItemProps[] = [
      { label: 'Push', value: 'push', disabled: IsDisabledPush },
      { label: 'Whatsapp', value: 'whatsapp', disabled: !b2bSendingChannelWhatsAppOptionEnabled },
    ];

    if (channelsData) {
      const filteredOptions = defaultSendingChannels?.map((option) => {
        const isInvalid = isChannelBlocked(
          option.value,
          channelsData.data ? channelsData.data : { whatsapp: false }
        );

        return {
          ...option,
          isInvalid,
          disabled: loadingChannels || option.disabled,
          invalidMessage: `Este cliente está restrito a receber${'\n'}oportunidades por este canal.`,
        };
      });

      return filteredOptions;
    }

    return defaultSendingChannels?.map((option) => ({
      ...option,
      disabled: loadingChannels || option.disabled,
    }));
  }, [
    b2bSendingChannelPushOptionEnabled,
    b2bSendingChannelWhatsAppOptionEnabled,
    channelsData,
    forceDisablePush,
    loadingChannels,
  ]);

  const sendingChannelsOptions: SendingChannelItemProps[] = useMemo(
    () => getChannelOptionsAvailable(),
    [getChannelOptionsAvailable]
  );

  const sendingChannelsOptionsSelected = sendingChannelsOptions.reduce((acc: string[], channel) => {
    if (channel.isInvalid || channel.disabled) {
      return acc;
    }

    acc.push(channel.value);

    return acc;
  }, []);

  const [sendingChannelsSelected, setSendingChannelsSelected] = useState(
    sendingChannelsOptionsSelected
  );

  const onChannelSelectChange = (value: string, selected: boolean) => {
    if (selected) {
      return setSendingChannelsSelected([...sendingChannelsSelected, value]);
    }
    const filteredOptions = sendingChannelsSelected?.filter((channel) => channel !== value);
    setSendingChannelsSelected(filteredOptions);
  };

  const onClearAllSelection = (newSelections: string[] = []) => {
    setSendingChannelsSelected(newSelections);
  };

  useEffect(() => {
    setSendingChannelsSelected(sendingChannelsOptionsSelected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendingChannelsOptions]);

  return {
    sendingChannelsOptions,
    sendingChannelsSelected,
    onChannelSelectChange,
    onClearAllSelection,
  };
};

export type SendingChannelsController = ReturnType<typeof useSendingChannels>;
